import { useEffect, useRef } from 'react';
import { Viewer } from 'mapillary-js';
import { Camera } from 'lucide-react';
import type { CurrentImage } from '@geoguess/shared';

const ACCESS_TOKEN = (import.meta.env.VITE_MAPILLARY_TOKEN as string | undefined) ?? '';

export function Pano({ image }: { image: CurrentImage }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<Viewer | null>(null);

  useEffect(() => {
    if (image.id === 'placeholder') return;
    if (!ACCESS_TOKEN) {
      // Without a client-side mapillary token we can't render the viewer either —
      // backend will already have served a placeholder, but if it didn't (token only
      // on backend) we just bail.
      return;
    }
    const el = ref.current;
    if (!el) return;
    // Discord activities can't fetch external hosts directly — every URL must go
    // through the dev-portal URL mappings (/.proxy/mly-graph → graph.mapillary.com,
    // etc). We point mapillary-js at relative paths on the current host so the
    // resulting URLs work both in Discord (proxied) and direct (nginx forwards
    // /mly-* to the upstream Mapillary hosts).
    const viewer = new Viewer({
      accessToken: ACCESS_TOKEN,
      container: el,
      imageId: image.id,
      component: { cover: false, attribution: true, bearing: true, zoom: true },
      urlOptions: {
        scheme: window.location.protocol.replace(':', ''),
        apiHost: window.location.host + '/mly-graph',
        imageTileHost: window.location.host + '/mly-tiles',
      },
    });
    viewerRef.current = viewer;
    return () => {
      viewer.remove();
      viewerRef.current = null;
    };
  }, [image.id]);

  useEffect(() => {
    if (!viewerRef.current || image.id === 'placeholder') return;
    viewerRef.current.moveTo(image.id).catch(() => {
      /* swallow — usually means image was unloaded between renders */
    });
  }, [image.id]);

  if (image.id === 'placeholder' || !ACCESS_TOKEN) {
    return (
      <div className="grid h-full w-full place-items-center bg-panel text-center">
        <div className="max-w-sm p-6">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-panel-2 ring-1 ring-border">
            <Camera className="h-6 w-6 text-muted" />
          </div>
          <p className="mt-4 text-[14px] text-muted">
            {image.id === 'placeholder'
              ? 'Kein Mapillary-Token konfiguriert — Platzhalter-Standort.'
              : 'Mapillary-Token fehlt im Frontend-Build.'}
          </p>
          <p className="mt-2 text-[12px] text-muted-2">
            Wahrer Ort (Debug): {image.lat.toFixed(3)}, {image.lng.toFixed(3)}
          </p>
        </div>
      </div>
    );
  }

  return <div ref={ref} className="mly-container" />;
}
